import useTranslation from 'next-translate/useTranslation';
import { datadogRum } from '@datadog/browser-rum';
import { useState, useContext } from 'react';
import { Button, Divider } from '@arkestro/arkestro-design-system';
import { Input } from 'antd-v3';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { deeplinkLogin, sendResetPasswordEmail } from '@api/AuthApi';
import { alertMessage } from 'utils/common';
import { UserContext } from 'components/providers/UserProvider/UserProvider';
import RedirectService from 'services/RedirectService';
import styles from './DeeplinkLoginForm.module.scss';

const DeeplinkLoginForm = props => {
  const { t } = useTranslation('common');
  const user = useContext(UserContext);
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const { id } = props;

  const handleForgotPassword = e => {
    e.preventDefault();
    setIsSubmitting(true);

    sendResetPasswordEmail({ email })
      .then(() => {
        alertMessage(t('forgot_password.messages.success'), 'success', 6);

        setTimeout(() => {
          setIsSubmitting(false);
          props.onModalClose();
        }, 500);
      })
      .catch(() => {
        setTimeout(() => {
          setIsSubmitting(false);
          props.onModalClose();
        }, 500);
      });
  };

  const handleSubmit = e => {
    e.preventDefault();
    deeplinkLogin({ email, id, password })
      .then(results => {
        setIsSubmitting(false);

        if (results && results.data && results.data.errors) {
          alertMessage(results.data.errors[0], 'error', 6);
          return;
        }

        if (!results.data || !results.data.current_user) {
          const stringMessage = `Email: ${email} - Status: ${results?.status}`;
          const errorResponse = { ...results, email };

          datadogRum.addError(
            new Error('Broken Login Flow', { cause: errorResponse }),
            { message: `Broken Login Flow - ${stringMessage}` }
          );
        }

        const { current_user: currentUser } = results.data;

        // Set the current user after successful sign in.
        user.setUserProperty(
          {
            ...currentUser,
            hasFailedHealthCheck: false,
          },
          () => {
            if (!results.data.is_invited) {
              router.push('/bid_requests');
            } else {
              RedirectService.redirectAuthedUser({ router, user: currentUser });
            }
          }
        );
      })
      .catch(err => {
        setIsSubmitting(false);

        datadogRum.addError('Broken Login Flow', {
          err,
          error: new Error('Broken Login Flow')
        });

        alertMessage(t('login.problem_signing_in'), 'error', 6);
      });
  };

  if (!user.hasAttemptedAuth) return null;

  return (
    <>
      {!isForgotPassword && (
        <>
          <div className={styles.title}>
            <b>{t('general.sign_in')}</b>
          </div>
          <div className={styles.subtitle}>{t('login.please_login')}</div>
          <label className={styles.inputLabel} htmlFor="user_email">
            {t('general.email')}
          </label>
          <Input
            autoFocus
            id="user_email"
            name="user[email]"
            onChange={e => setEmail(e.target.value)}
            placeholder={t('login.enter_email')}
            size="large"
            value={email}
          />

          <label className={styles.inputLabel} htmlFor="user_password">
            {t('general.password')}
          </label>

          <Input.Password
            id="user_password"
            name="user[password]"
            onChange={e => setPassword(e.target.value)}
            placeholder={t('login.enter_password')}
            size="large"
            type="password"
            value={password}
          />
        </>
      )}
      {!isForgotPassword && (
        <Button
          className="margin-top-medium"
          onClick={() => setIsForgotPassword(true)}
          type="link"
        >
          {t('login.forgot_password')}?
        </Button>
      )}
      {isForgotPassword && (
        <>
          <div className="title" style={{ fontSize: '20px' }}>
            <b>{t('login.forgot_password')}</b>
          </div>
          <div className="title" style={{ fontSize: '15px' }}>
            {t('login.next_steps')}
          </div>
          <label
            htmlFor="user_password"
            style={{ fontSize: '15px', marginTop: '15px' }}
          >
            {t('general.email')}
          </label>
          <Input
            autoFocus
            id="user_email"
            name="user[email]"
            onChange={e => setEmail(e.target.value)}
            placeholder={t('login.enter_email')}
            size="large"
            value={email}
          />
        </>
      )}
      <Divider />
      {isForgotPassword ? (
        <Button
          ghost
          onClick={() => setIsForgotPassword(false)}
          size="large"
          type="secondary"
        >
          {t('general.back')}
        </Button>
      ) : (
        <Button
          ghost
          onClick={props.onModalClose}
          size="large"
          type="secondary"
        >
          {t('general.cancel')}
        </Button>
      )}
      {isForgotPassword ? (
        <Button data-testid="deeplink-reset-password" onClick={handleForgotPassword} size="large" type="secondary">
          {t('general.continue')}
        </Button>
      ) : (
        <Button
          data-testid="deeplink-login-submit"
          disabled={isSubmitting}
          onClick={handleSubmit}
          size="large"
          type="secondary"
        >
          {t('general.sign_in')}
        </Button>
      )}
    </>
  );
};

DeeplinkLoginForm.propTypes = {
  id: PropTypes.string.isRequired,
  onModalClose: PropTypes.func.isRequired,
};

export default DeeplinkLoginForm;
